import { money, type Dec } from "@ftr/tax-core";

export type Parsed = { kind: "value"; value: Dec } | { kind: "empty" } | { kind: "error"; message: string };

export function parseMoney(raw: string): Parsed {
  if (raw.trim() === "") return { kind: "empty" };
  try {
    return { kind: "value", value: money(raw) };
  } catch {
    return { kind: "error", message: "Enter an amount like 12,500 or 12500.00" };
  }
}

/**
 * Collect field values. Required fields must be filled; optional ones may be left blank and are
 * then counted as $0 with that assumption listed, so a blank is never silently treated as zero.
 * A negative amount is an error unless the field allows it, so nothing is computed from it.
 */
export function collect<K extends string>(
  fields: Record<K, { raw: string; label: string; optional?: boolean; allowNegative?: boolean }>,
): { values: Record<K, Dec>; missing: string[]; errors: string[]; assumedZero: string[] } {
  const values = {} as Record<K, Dec>;
  const missing: string[] = [];
  const errors: string[] = [];
  const assumedZero: string[] = [];
  for (const key of Object.keys(fields) as K[]) {
    const f = fields[key];
    const p = parseMoney(f.raw);
    if (p.kind === "value" && p.value.lessThan(0) && !f.allowNegative) errors.push(f.label);
    else if (p.kind === "value") values[key] = p.value;
    else if (p.kind === "error") errors.push(f.label);
    else if (f.optional) {
      values[key] = money("0");
      assumedZero.push(f.label);
    } else missing.push(f.label);
  }
  return { values, missing, errors, assumedZero };
}
